import {
  ActionSchema,
  DynamicBooleanSchema,
  DynamicStringSchema,
  DynamicValueSchema,
} from '@a2ui/web_core/v0_9';
import { createComponentImplementation } from '@a2ui/react/v0_9';
import { defineA2uiComponentSchema } from '../../a2ui-schema';
import {
  ClaimReport,
  type ClaimReportPhase,
  type ClaimReportPolicy,
  type ClaimReportSituation,
} from './ClaimReport';
import type { ClaimReportField } from './claimReportRules';

interface ResolvedProps {
  readonly phase?: string;
  readonly title?: string;
  readonly policy?: ClaimReportPolicy | null;
  readonly situations?: ClaimReportSituation[];
  readonly fields?: ClaimReportField[];
  readonly onSend?: () => void;
  // Bound to `/locked` like AskQuestions' `locked`: A2UISurfaceList writes it once
  // this card is sent or a newer turn arrives, and the binding is what re-renders
  // the card so it freezes instead of accepting late picks.
  readonly locked?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const schema = defineA2uiComponentSchema({
  phase: DynamicStringSchema,
  title: DynamicStringSchema,
  policy: DynamicValueSchema,
  situations: DynamicValueSchema,
  fields: DynamicValueSchema,
  onSend: ActionSchema,
  locked: DynamicBooleanSchema,
}) as any;

const VALID_PHASES: readonly ClaimReportPhase[] = ['choices', 'details'];

function resolvePhase(value: string | undefined): ClaimReportPhase {
  return (VALID_PHASES as readonly string[]).includes(value ?? '')
    ? (value as ClaimReportPhase)
    : 'choices';
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

/** Reads `/answers` directly off the host data model — the same pattern
 *  AskQuestions.a2ui's `readSubmitted` uses for a value the agent never binds
 *  as a component prop. Both `ClaimReport:SendClaimChoices`'s context
 *  (`answers:{path:"/answers"}`) and this card's own prefill read from this
 *  one place, so they can't drift. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function readAnswers(context: any): Record<string, unknown> {
  const value = context?.dataContext?.dataModel?.get?.('/answers');
  return value && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : {};
}

/** Whether the user actually sent this card (vs. it merely going stale
 *  unanswered) — written by A2UISurfaceList at `/submitted`, read directly
 *  off the data model the same way AskQuestions' `readSubmitted` does. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function readSubmitted(context: any): boolean {
  return context?.dataContext?.dataModel?.get?.('/submitted') === true;
}

export const ClaimReportImpl = createComponentImplementation(
  { name: 'ClaimReport', schema },
  ({
    props,
    context,
  }: {
    readonly props: ResolvedProps;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    readonly buildChild: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    readonly context: any;
  }) => (
    <ClaimReport
      phase={resolvePhase(props.phase)}
      title={props.title}
      policy={props.policy ?? null}
      situations={asArray<ClaimReportSituation>(props.situations)}
      fields={asArray<ClaimReportField>(props.fields)}
      onSend={props.onSend}
      initialAnswers={readAnswers(context)}
      onWrite={(path, value) => context.dataContext.set(path, value)}
      locked={props.locked === true}
      submitted={readSubmitted(context)}
    />
  )
);
